import type { Direction } from "../engine/direction";
import { isLookAtHorizontalDirection, isPointsToDirection, isSomethingWithState } from "../engine/interfaces";
import type { PhysicalThing } from "../engine/physicalThing";
import { MultiTileThingPart } from "../engine/things/multitile/multiTileThingPart";

/**
 * Returns the current "look direction" of a thing for animation tracking.
 * Used by DrawableManager to detect direction changes that should trigger
 * an animation reload (so the rendered sprite reflects the new orientation).
 *
 * Priority: PointsToDirection before LookAtHorizontalDirection — entities like
 * BigSquidTentacle implement both, and the tentacle direction (PointsToDirection)
 * is what should drive sprite selection, not the mob's horizontal facing.
 *
 * Multi-tile entities (MiningRobot, ArtilleryRobot) store their look direction
 * on the MultiTileThingCore, but the things tracked by DrawableManager are the
 * MultiTileThingParts that occupy cells. Look through to the core so direction
 * changes on the core trigger sprite reloads on the parts.
 */
export function getCurrentDirection(thing: PhysicalThing): Direction | null {
	if (thing instanceof MultiTileThingPart) {
		const core = thing.controller.getThing();
		if (isPointsToDirection(core)) return core.getDirection();
		if (isLookAtHorizontalDirection(core)) return core.getLookingAtHorizontalDirection();
		return null;
	}
	if (isPointsToDirection(thing)) return thing.getDirection();
	if (isLookAtHorizontalDirection(thing)) return thing.getLookingAtHorizontalDirection();
	return null;
}

export function getCurrentState(thing: PhysicalThing): string | null {
	if (isSomethingWithState(thing)) {
		const state = thing.getState();
		return state != null ? String(state) : null;
	}
	return null;
}
